import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/back-button';
import { CheckCircle, XCircle, Loader2, Mail, Globe } from 'lucide-react';
import { dfyService, type OrderStatusResponse } from '@/lib/services/dfy';

type OrderStatus = 'pending' | 'paid' | 'provisioning' | 'completed' | 'failed' | 'expired';

function getStatusColor(status: string): string {
  switch (status) {
    case 'completed':
    case 'ready':
    case 'verified':
      return 'text-success-foreground';
    case 'failed':
    case 'expired':
      return 'text-danger-foreground';
    case 'provisioning':
    case 'purchasing':
    case 'dns_configured':
      return 'text-info-foreground';
    default:
      return 'text-muted-foreground';
  }
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'completed' || status === 'ready' || status === 'verified') {
    return <CheckCircle className="h-5 w-5 text-success-foreground" />;
  }
  if (status === 'failed' || status === 'expired') {
    return <XCircle className="h-5 w-5 text-danger-foreground" />;
  }
  return <Loader2 className="h-5 w-5 text-info-foreground animate-spin" />;
}

export default function DfySuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [orderData, setOrderData] = useState<OrderStatusResponse | null>(null);
  const [waitingForOrder, setWaitingForOrder] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollCount = useRef(0);

  const fetchOrderStatus = useCallback(async (): Promise<boolean> => {
    if (!sessionId) {
      setError('No session ID provided');
      setWaitingForOrder(false);
      return false;
    }

    try {
      const data = await dfyService.getOrderBySessionId(sessionId);
      setOrderData(data);
      setWaitingForOrder(false);
      setError(null);
      return true;
    } catch (err: any) {
      // 404 means order not created yet - keep polling
      if (err?.response?.status === 404 || err?.message?.includes('404')) {
        return false;
      }
      console.error('Failed to fetch order status:', err);
      setError('Failed to load order status');
      setWaitingForOrder(false);
      return false;
    }
  }, [sessionId]);

  // Initial fetch and poll until order exists
  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      setWaitingForOrder(false);
      return;
    }

    const poll = async () => {
      const found = await fetchOrderStatus();
      if (!found && pollCount.current < 30) {
        // Poll for up to 30 attempts (~30 seconds)
        pollCount.current += 1;
        setTimeout(poll, 1000);
      } else if (!found) {
        setError('Order processing is taking longer than expected. Please check back later.');
        setWaitingForOrder(false);
      }
    };

    poll();
  }, [sessionId, fetchOrderStatus]);

  // Continue polling for status updates while provisioning
  useEffect(() => {
    if (!orderData) return;

    const status = orderData.order.status as OrderStatus;
    const shouldPoll = status === 'paid' || status === 'provisioning';

    if (!shouldPoll) return;

    const interval = setInterval(fetchOrderStatus, 3000);
    return () => clearInterval(interval);
  }, [orderData, fetchOrderStatus]);

  if (waitingForOrder) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">Processing your payment...</p>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="px-4 mt-7">
        <div className="flex items-center gap-4 mb-6">
          <BackButton href="/dashboard/email-accounts" tooltip="Back to Email Accounts" />
          <h1 className="text-xl font-semibold">Order Summary</h1>
        </div>
        <Card className="p-6">
          <p className="text-danger-foreground">{error || 'Order not found'}</p>
          <Button asChild className="mt-4">
            <Link to="/dashboard/email-accounts">Go to Email Accounts</Link>
          </Button>
        </Card>
      </div>
    );
  }

  const { order, domains, accounts } = orderData;
  const isCompleted = order.status === 'completed';
  const isFailed = order.status === 'failed' || order.status === 'expired';
  const isProcessing = order.status === 'paid' || order.status === 'provisioning';

  return (
    <>
      <div className="px-4 mt-7">
        <div className="flex items-center gap-4">
          <BackButton href="/dashboard/email-accounts" tooltip="Back to Email Accounts" />
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">Order Summary</h1>
            {isCompleted && (
              <span className="text-sm bg-success-surface-strong text-success-foreground-strong px-2.5 py-0.5 rounded-full">
                Completed
              </span>
            )}
            {isFailed && (
              <span className="text-sm bg-danger-surface-strong text-danger-foreground-strong px-2.5 py-0.5 rounded-full">
                {order.status === 'expired' ? 'Expired' : 'Failed'}
              </span>
            )}
            {isProcessing && (
              <span className="text-sm bg-info-surface-strong text-info-foreground-strong px-2.5 py-0.5 rounded-full flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />
                Processing
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-8 flex justify-center mt-page-top pb-8">
        <div className="w-full max-w-4xl space-y-6">
          {/* Order Summary */}
          <Card className="p-6">
            <h2 className="text-lg font-medium mb-4">Order Details</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Order ID:</span>
                <span className="ml-2 font-mono">{order.id.slice(0, 8)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total:</span>
                <span className="ml-2">${(order.totalAmountCents / 100).toFixed(2)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Domains:</span>
                <span className="ml-2">{order.domainCount}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Accounts:</span>
                <span className="ml-2">{order.accountCount}</span>
              </div>
            </div>
            {order.errorMessage && (
              <p className="mt-4 text-sm text-danger-foreground">{order.errorMessage}</p>
            )}
          </Card>

          {/* Domains Progress */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5" />
              <h2 className="text-lg font-medium">Domains</h2>
            </div>
            <div className="space-y-3">
              {domains.map((domain) => (
                <div key={domain.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <StatusIcon status={domain.status} />
                    <span>{domain.domain}</span>
                  </div>
                  <span className={`text-sm capitalize ${getStatusColor(domain.status)}`}>
                    {domain.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Email Accounts Progress */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5" />
              <h2 className="text-lg font-medium">Email Accounts</h2>
            </div>
            <div className="space-y-3">
              {accounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <StatusIcon status={account.status} />
                    <div>
                      <span>{account.emailAddress}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ({account.personaFirstName} {account.personaLastName})
                      </span>
                    </div>
                  </div>
                  <span className={`text-sm capitalize ${getStatusColor(account.status)}`}>
                    {account.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Action Button */}
          {isCompleted && (
            <div className="flex justify-center">
              <Button asChild variant="primary">
                <Link to="/dashboard/email-accounts">View Email Accounts</Link>
              </Button>
            </div>
          )}

          {isFailed && (
            <div className="flex justify-center gap-4">
              <Button asChild variant="secondary">
                <Link to="/dashboard/email-accounts">Go to Email Accounts</Link>
              </Button>
              <Button asChild variant="primary">
                <Link to="/dashboard/email-accounts/dfy">Try Again</Link>
              </Button>
            </div>
          )}

          <div className="flex justify-center mt-8">
            <Button asChild variant="secondary">
              <Link to="/dashboard/email-accounts">Return to Email Accounts</Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
